import React, { useCallback, useRef, useEffect } from 'react'
import { Input, Button, Select, Card, Typography, Form, DatePicker, Space, Divider } from 'antd'
import { SearchOutlined, SendOutlined, ReloadOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import Inputmask from 'inputmask'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import { useLocation } from 'react-router-dom'
import { createSimpleStore } from 'react-simple-reducer'
import locale from 'antd/es/date-picker/locale/pt_BR'
import { toast } from 'sonner'
import api from '../../api'
import { CopyButton } from '../../components/CopyButton'
import { Show } from '../../components/Basic'

// Configure dayjs with required plugins for Ant Design
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.locale('pt-br')

const { Option } = Select
const { Title: AntTitle, Text } = Typography

// Brazilian states in alphabetical order
const BRAZILIAN_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]

interface MatriculaData {
  id: number
  statusDetran: string
  uf: string
  dataInicioCurso?: string
  dataFimCurso?: string
  dataInicioModuloTeorico?: string
  dataFimModuloTeorico?: string
  dataInicioModuloPratico?: string
  dataFimModuloPratico?: string
  identificacaoCertificado?: string
  identificacaoCertificadoRetornado?: string
  identificacaoCertificadoRetornadoTeorico?: string
  dataMatriculaDetran?: string
  ufMatriculaDetran?: string
  aluno: {
    id: number
    nome: string
    cpf: string
    email?: string
    telefone?: string
    dataNascimento?: string
    cnh?: string
  }
  curso: {
    id: number
    descricao: string
    nomeReduzido: string
  }
}

interface ConcluirFormData {
  targetState: string
  modulo: 'TEORICO' | 'PRATICO'
  dataInicioCurso?: string
  dataFimCurso?: string
  dataFimModuloTeorico?: string
  dataFimModuloPratico?: string
}

interface MatricularFormData {
  targetState: string
}

interface CancelarFormData {
  targetState: string
  modulo: 'TEORICO' | 'PRATICO'
  dataInicio?: string
  cpf: string
  dataNascimento: string
  cursoId: number
  tipo: 'MATRICULA' | 'CONCLUSAO'
  cnh?: string
}

const DetranActionsStore = createSimpleStore(
  {
    searchMatriculaId: '',
    matriculaData: null,
    loading: false,
    submitting: false,
    concluirForm: {
      targetState: '',
      modulo: 'TEORICO',
      dataInicioCurso: '',
      dataFimCurso: '',
      dataFimModuloTeorico: '',
      dataFimModuloPratico: '',
    } as ConcluirFormData,
    matricularForm: {
      targetState: '',
    },
    cancelarForm: {
      targetState: '',
      modulo: 'TEORICO',
      dataInicio: '',
      cpf: '',
      dataNascimento: '',
      cursoId: 0,
      cnh: '',
      tipo: 'MATRICULA' as 'MATRICULA' | 'CONCLUSAO',
    } as CancelarFormData,
  },
  {
    setSearchMatriculaId(state, value: string) {
      state.searchMatriculaId = value
    },
    setMatriculaData(state, data: MatriculaData | null) {
      state.matriculaData = data
    },
    setLoading(state, loading: boolean) {
      state.loading = loading
    },
    setSubmitting(state, submitting: boolean) {
      state.submitting = submitting
    },
    updateConcluirForm(state, { field, value }: { field: keyof ConcluirFormData; value: any }) {
      state.concluirForm[field] = value
    },
    updateMatricularForm(state, { field, value }: { field: keyof MatricularFormData; value: any }) {
      state.matricularForm[field] = value
    },
    updateCancelarForm(state, { field, value }: { field: keyof CancelarFormData; value: any }) {
      state.cancelarForm[field as any] = value
    },
    setMatriculaAndForm(
      state,
      { matriculaData, formData }: { matriculaData: MatriculaData; formData: ConcluirFormData }
    ) {
      state.matriculaData = matriculaData
      state.concluirForm = formData
      state.matricularForm.targetState = matriculaData.uf || ''
      state.cancelarForm = {
        targetState: matriculaData.uf || '',
        modulo: 'TEORICO',
        dataInicio: '',
        cpf: matriculaData.aluno.cpf || '',
        dataNascimento: matriculaData.aluno.dataNascimento || '',
        cursoId: matriculaData.curso.id || 0,
        cnh: matriculaData.aluno.cnh || '',
        tipo: 'MATRICULA',
      }
    },
    resetForm(state) {
      state.concluirForm = {
        targetState: '',
        modulo: 'TEORICO',
        dataInicioCurso: '',
        dataFimCurso: '',
        dataFimModuloTeorico: '',
        dataFimModuloPratico: '',
      }
      state.matricularForm = {
        targetState: '',
      }
      state.cancelarForm = {
        targetState: '',
        modulo: 'TEORICO',
        dataInicio: '',
        cpf: '',
        dataNascimento: '',
        cursoId: 0,
        tipo: 'MATRICULA',
      }
    },
  },
  {
    thunks: {
      searchMatricula({ searchMatriculaId }: { searchMatriculaId: string }) {
        return async (dispatch) => {
          if (!searchMatriculaId) {
            toast.error('Digite um ID de matrícula')
            return
          }

          dispatch(DetranActionsStore.actions.setLoading(true))

          try {
            const matricula = await api.DetranLogs.getMatricula(searchMatriculaId)

            // Pre-fill form with current database values
            const formData: ConcluirFormData = {
              targetState: matricula.ufMatriculaDetran || matricula.uf || '',
              modulo: 'TEORICO',
              dataInicioCurso: matricula.dataInicioCurso
                ? dayjs(matricula.dataInicioCurso).format('YYYY-MM-DD')
                : '',
              dataFimCurso: matricula.dataFimCurso
                ? dayjs(matricula.dataFimCurso).format('YYYY-MM-DD')
                : '',
              dataFimModuloTeorico: matricula.dataFimModuloTeorico
                ? dayjs(matricula.dataFimModuloTeorico).format('YYYY-MM-DD')
                : '',
              dataFimModuloPratico: matricula.dataFimModuloPratico
                ? dayjs(matricula.dataFimModuloPratico).format('YYYY-MM-DD')
                : '',
            }

            dispatch(
              DetranActionsStore.actions.setMatriculaAndForm({
                matriculaData: matricula,
                formData,
              })
            )
          } catch (error: any) {
            toast.error(error.message || 'Matrícula não encontrada')
            dispatch(DetranActionsStore.actions.setMatriculaData(null))
          } finally {
            dispatch(DetranActionsStore.actions.setLoading(false))
          }
        }
      },

      submitConcluir() {
        return async (dispatch, getState) => {
          const { matriculaData, concluirForm } = getState()

          if (!matriculaData) {
            toast.error('Busque uma matrícula primeiro')
            return
          }

          dispatch(DetranActionsStore.actions.setSubmitting(true))

          try {
            const response = await api.DetranLogs.concluir({
              matriculaId: matriculaData.id,
              ...concluirForm,
            })

            if (response.success) {
              toast.success(response.successMessage || 'Operação realizada com sucesso')
            } else {
              toast.error(response.errorMessage || 'Erro na operação')
            }
          } catch (error: any) {
            toast.error(error.message || 'Erro ao enviar conclusão')
          } finally {
            dispatch(DetranActionsStore.actions.setSubmitting(false))
          }
        }
      },

      submitMatricular() {
        return async (dispatch, getState) => {
          const { matriculaData, matricularForm } = getState()

          if (!matriculaData) {
            toast.error('Busque uma matrícula primeiro')
            return
          }

          dispatch(DetranActionsStore.actions.setSubmitting(true))

          try {
            const response = await api.DetranLogs.matricular({
              matriculaId: matriculaData.id,
              ...matricularForm,
            })

            if (response.success) {
              toast.success(response.successMessage || 'Operação realizada com sucesso')
            } else {
              toast.error(response.errorMessage || 'Erro na operação')
            }
          } catch (error: any) {
            toast.error(error.message || 'Erro ao enviar matrícula')
          } finally {
            dispatch(DetranActionsStore.actions.setSubmitting(false))
          }
        }
      },

      submitCancelar() {
        return async (dispatch, getState) => {
          const { cancelarForm } = getState()

          dispatch(DetranActionsStore.actions.setSubmitting(true))

          try {
            const response = await api.DetranLogs.cancelar(cancelarForm)

            if (response.success) {
              toast.success(response.successMessage || 'Operação realizada com sucesso')
            } else {
              toast.error(response.errorMessage || 'Erro na operação')
            }
          } catch (error: any) {
            toast.error(error.message || 'Erro ao cancelar matrícula')
          } finally {
            dispatch(DetranActionsStore.actions.setSubmitting(false))
          }
        }
      },
    },
  }
)

export const DetranActions = () => {
  const location = useLocation()

  const initFn = useCallback(
    (dispatch: ReturnType<typeof DetranActionsStore.useDispatch>) => {
      const urlParams = new URLSearchParams(location.search)
      const matriculaId = urlParams.get('matriculaId')

      if (matriculaId) {
        dispatch(DetranActionsStore.actions.setSearchMatriculaId(matriculaId))
        dispatch(DetranActionsStore.thunks.searchMatricula({ searchMatriculaId: matriculaId }))
      }
    },
    [location.search]
  )

  return (
    <DetranActionsStore.Provider init={initFn}>
      <Container>
        <PageHeader>
          <PageTitle>Ações DETRAN</PageTitle>
        </PageHeader>

        <Search />
        <MatriculaInfo />
        <Concluir />
        <Matricular />
        <Cancelar />
      </Container>
    </DetranActionsStore.Provider>
  )
}

const Search = () => {
  const { searchMatriculaId, loading } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()

  const handleSearch = () => {
    dispatch(DetranActionsStore.thunks.searchMatricula({ searchMatriculaId }))
  }

  return (
    <SearchCard>
      <AntTitle level={4}>Buscar Matrícula</AntTitle>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          placeholder="ID da matrícula"
          value={searchMatriculaId}
          onChange={(e) =>
            dispatch(DetranActionsStore.actions.setSearchMatriculaId(e.target.value))
          }
          onPressEnter={handleSearch}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
          Buscar
        </Button>
      </Space.Compact>
    </SearchCard>
  )
}

const MatriculaInfo = () => {
  const { matriculaData } = DetranActionsStore.useState()

  if (!matriculaData) return null

  return (
    <MatriculaCard>
      <AntTitle level={4}>Dados da Matrícula</AntTitle>
      <MatriculaGrid>
        <MatriculaItem>
          <Label>Estudante:</Label>
          <Value>{matriculaData.aluno.nome}</Value>
        </MatriculaItem>
        <MatriculaItem>
          <Label>CPF:</Label>
          <Value style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{matriculaData.aluno.cpf}</span>
            {matriculaData.aluno.cpf && <CopyButton value={matriculaData.aluno.cpf} size="small" />}
          </Value>
        </MatriculaItem>
        <MatriculaItem>
          <Label>Curso:</Label>
          <Value>{matriculaData.curso.nomeReduzido}</Value>
        </MatriculaItem>
        <MatriculaItem>
          <Label>Status:</Label>
          <Value>{matriculaData.statusDetran}</Value>
        </MatriculaItem>
        <MatriculaItem>
          <Label>UF Atual:</Label>
          <Value>{matriculaData.uf}</Value>
        </MatriculaItem>
        <MatriculaItem>
          <Label>UF Matrícula DETRAN:</Label>
          <Value>{matriculaData.ufMatriculaDetran || 'N/A'}</Value>
        </MatriculaItem>
      </MatriculaGrid>

      <Divider />

      <DatesGrid>
        <DateItem>
          <Label>Data Início do Curso:</Label>
          <Value>
            {matriculaData.dataInicioCurso
              ? dayjs(matriculaData.dataInicioCurso).format('DD/MM/YYYY')
              : 'N/A'}
          </Value>
        </DateItem>
        <DateItem>
          <Label>Data Fim do Curso:</Label>
          <Value>
            {matriculaData.dataFimCurso
              ? dayjs(matriculaData.dataFimCurso).format('DD/MM/YYYY')
              : 'N/A'}
          </Value>
        </DateItem>
        <DateItem>
          <Label>Data Fim do Módulo Teórico:</Label>
          <Value>
            {matriculaData.dataFimModuloTeorico
              ? dayjs(matriculaData.dataFimModuloTeorico).format('DD/MM/YYYY')
              : 'N/A'}
          </Value>
        </DateItem>
        <DateItem>
          <Label>Data Fim do Módulo Prático:</Label>
          <Value>
            {matriculaData.dataFimModuloPratico
              ? dayjs(matriculaData.dataFimModuloPratico).format('DD/MM/YYYY')
              : 'N/A'}
          </Value>
        </DateItem>
      </DatesGrid>
    </MatriculaCard>
  )
}

const Concluir = () => {
  const { matriculaData, concluirForm, submitting } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()

  if (!matriculaData) return null

  const handleSubmit = () => {
    dispatch(DetranActionsStore.thunks.submitConcluir())
  }

  const handleReset = () => {
    dispatch(DetranActionsStore.actions.resetForm())
  }

  return (
    <ActionsCard>
      <AntTitle level={4}>Concluir Curso</AntTitle>
      <FormGrid
      //  key={`form-grid-${matriculaData?.id || 'empty'}`}
      >
        <FormItem>
          <Label>Estado de Destino *</Label>
          <Select
            value={concluirForm.targetState}
            onChange={(value) =>
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'targetState',
                  value,
                })
              )
            }
            style={{ width: '100%' }}
            placeholder="Digite ou selecione o estado"
            showSearch
            filterOption={(input, option) =>
              String(option?.children)?.toLowerCase().includes(input.toLowerCase()) || false
            }
            allowClear
          >
            {BRAZILIAN_STATES.map((state) => (
              <Option key={state} value={state}>
                {state}
              </Option>
            ))}
          </Select>
        </FormItem>

        <FormItem>
          <Label>Módulo *</Label>
          <Select
            value={concluirForm.modulo}
            onChange={(value) =>
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'modulo',
                  value,
                })
              )
            }
            style={{ width: '100%' }}
          >
            <Option value="TEORICO">Teórico</Option>
            <Option value="PRATICO">Prático</Option>
          </Select>
        </FormItem>

        <FormItem>
          <Label>Data Início do Curso</Label>
          <DatePicker
            format="DD/MM/YYYY"
            value={concluirForm.dataInicioCurso ? dayjs(concluirForm.dataInicioCurso) : null}
            onChange={(date) => {
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'dataInicioCurso',
                  value: date ? dayjs(date).format('YYYY-MM-DD') : '',
                })
              )
            }}
            style={{ width: '100%' }}
            locale={locale}
          />
        </FormItem>

        <FormItem>
          <Label>Data Fim do Curso</Label>
          <DatePicker
            format="DD/MM/YYYY"
            value={concluirForm.dataFimCurso ? dayjs(concluirForm.dataFimCurso) : null}
            onChange={(date) =>
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'dataFimCurso',
                  value: date ? dayjs(date).format('YYYY-MM-DD') : '',
                })
              )
            }
            style={{ width: '100%' }}
            locale={locale}
          />
        </FormItem>

        <FormItem>
          <Label>Data Fim do Módulo Teórico</Label>
          <DatePicker
            format="DD/MM/YYYY"
            value={
              concluirForm.dataFimModuloTeorico ? dayjs(concluirForm.dataFimModuloTeorico) : null
            }
            onChange={(date) =>
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'dataFimModuloTeorico',
                  value: date ? dayjs(date).format('YYYY-MM-DD') : '',
                })
              )
            }
            style={{ width: '100%' }}
            locale={locale}
          />
        </FormItem>

        <FormItem>
          <Label>Data Fim do Módulo Prático</Label>
          <DatePicker
            format="DD/MM/YYYY"
            value={
              concluirForm.dataFimModuloPratico ? dayjs(concluirForm.dataFimModuloPratico) : null
            }
            onChange={(date) =>
              dispatch(
                DetranActionsStore.actions.updateConcluirForm({
                  field: 'dataFimModuloPratico',
                  value: date ? dayjs(date).format('YYYY-MM-DD') : '',
                })
              )
            }
            style={{ width: '100%' }}
            locale={locale}
          />
        </FormItem>
      </FormGrid>

      <FormActions>
        <Button onClick={handleReset} icon={<ReloadOutlined />}>
          Limpar
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          icon={<SendOutlined />}
          disabled={!concluirForm.targetState || !concluirForm.modulo}
        >
          Enviar Conclusão
        </Button>
      </FormActions>
    </ActionsCard>
  )
}

const Matricular = () => {
  const { matriculaData, matricularForm, submitting } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()

  if (!matriculaData) return null

  const handleSubmit = () => {
    dispatch(DetranActionsStore.thunks.submitMatricular())
  }

  return (
    <MatricularCard>
      <AntTitle level={4}>Matricular</AntTitle>
      <FormGrid>
        <FormItem>
          <Label>Estado de Matrícula *</Label>
          <Select
            value={matricularForm.targetState}
            onChange={(value) =>
              dispatch(
                DetranActionsStore.actions.updateMatricularForm({
                  field: 'targetState',
                  value,
                })
              )
            }
            style={{ width: '100%' }}
            placeholder="Digite ou selecione o estado"
            showSearch
            filterOption={(input, option) =>
              String(option?.children)?.toLowerCase().includes(input.toLowerCase()) || false
            }
            allowClear
          >
            {BRAZILIAN_STATES.map((state) => (
              <Option key={state} value={state}>
                {state}
              </Option>
            ))}
          </Select>
        </FormItem>
      </FormGrid>

      <FormActions>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          icon={<SendOutlined />}
          disabled={!matricularForm.targetState}
        >
          Enviar Matrícula
        </Button>
      </FormActions>
    </MatricularCard>
  )
}

const DataNascimentoInput = () => {
  const { cancelarForm } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()
  const inputRef = useRef<any>(null)

  useEffect(() => {
    let maskInstance: any = null
    if (inputRef.current) {
      const inputElement = inputRef.current.nativeElement?.querySelector?.('input')
      if (inputElement) {
        maskInstance = Inputmask('99/99/9999').mask(inputElement)
      }
    }

    return () => {
      if (maskInstance) {
        maskInstance.remove()
      }
    }
  }, [])

  const handleChange = (date: any, dateString: string) => {
    dispatch(
      DetranActionsStore.actions.updateCancelarForm({
        field: 'dataNascimento',
        value: date ? dayjs(date).format('YYYY-MM-DD') : '',
      })
    )
  }

  return (
    <DatePicker
      ref={inputRef}
      format="DD/MM/YYYY"
      value={cancelarForm.dataNascimento ? dayjs(cancelarForm.dataNascimento) : null}
      onChange={handleChange}
      locale={locale}
      style={{ width: '100%' }}
      placeholder="DD/MM/AAAA"
      inputReadOnly={false}
      allowClear
    />
  )
}

const CursoSelect = () => {
  const { cancelarForm } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()
  const [cursos, setCursos] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)

  useEffect(() => {
    fetchCursos()
  }, [])

  const fetchCursos = async () => {
    setLoading(true)
    try {
      const cursos = await api.Cursos.get({ tipoCurso: 'CREDENCIADO_DETRAN' } as any)
      setCursos(cursos.rows)
    } catch (error) {
      console.error('Error fetching cursos:', error)
      toast.error('Erro ao carregar cursos')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (value: number) => {
    dispatch(
      DetranActionsStore.actions.updateCancelarForm({
        field: 'cursoId',
        value,
      })
    )
  }

  return (
    <Select
      value={cancelarForm.cursoId || undefined}
      onChange={handleChange}
      style={{ width: '100%' }}
      placeholder="Selecione o curso"
      showSearch
      optionFilterProp="children"
      filterOption={(input, option) =>
        String(option?.children || '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      loading={loading}
      allowClear
    >
      {cursos.map((curso) => (
        <Option key={curso.id} value={curso.id}>
          {curso.nomeReduzido || curso.nome}
        </Option>
      ))}
    </Select>
  )
}

const Cancelar = () => {
  const { cancelarForm, submitting } = DetranActionsStore.useState()
  const dispatch = DetranActionsStore.useDispatch()

  const handleSubmit = () => {
    dispatch(DetranActionsStore.thunks.submitCancelar())
  }

  return (
    <CancelarCard>
      <AntTitle level={4}>Cancelar Matrícula</AntTitle>
      <FormGrid>
        <FormItem>
          <Label>Estado de Destino *</Label>
          <Select
            value={cancelarForm.targetState}
            onChange={(value) =>
              dispatch(
                DetranActionsStore.actions.updateCancelarForm({
                  field: 'targetState',
                  value,
                })
              )
            }
            style={{ width: '100%' }}
            placeholder="Digite ou selecione o estado"
            showSearch
            filterOption={(input, option) =>
              String(option?.children)?.toLowerCase().includes(input.toLowerCase()) || false
            }
            allowClear
          >
            {BRAZILIAN_STATES.map((state) => (
              <Option key={state} value={state}>
                {state}
              </Option>
            ))}
          </Select>
        </FormItem>

        <FormItem>
          <Label>Módulo *</Label>
          <Select
            value={cancelarForm.modulo}
            onChange={(value) =>
              dispatch(
                DetranActionsStore.actions.updateCancelarForm({
                  field: 'modulo',
                  value,
                })
              )
            }
            style={{ width: '100%' }}
          >
            <Option value="TEORICO">Teórico</Option>
            <Option value="PRATICO">Prático</Option>
          </Select>
        </FormItem>

        <FormItem>
          <Label>CPF *</Label>
          <Input
            value={cancelarForm.cpf}
            onChange={(e) =>
              dispatch(
                DetranActionsStore.actions.updateCancelarForm({
                  field: 'cpf',
                  value: e.target.value,
                })
              )
            }
            placeholder="000.000.000-00"
          />
        </FormItem>

        <FormItem>
          <Label>Data de Nascimento *</Label>
          <DataNascimentoInput />
        </FormItem>

        <FormItem>
          <Label>Curso *</Label>
          <CursoSelect />
        </FormItem>

        <FormItem>
          <Label style={{ whiteSpace: 'nowrap' }}>
            Data Início do Módulo <small>(Obrigatório para PE)</small>
          </Label>
          <DatePicker
            format="DD/MM/YYYY"
            value={cancelarForm.dataInicio ? dayjs(cancelarForm.dataInicio) : null}
            onChange={(date) =>
              dispatch(
                DetranActionsStore.actions.updateCancelarForm({
                  field: 'dataInicio',
                  value: date ? dayjs(date).format('YYYY-MM-DD') + ' 00:00' : '',
                })
              )
            }
            style={{ width: '100%' }}
            locale={locale}
          />
        </FormItem>
        <Show condition={cancelarForm.targetState === 'SP'}>
          <FormItem>
            <Label>Tipo</Label>
            <Select
              onChange={(e) =>
                dispatch(DetranActionsStore.actions.updateCancelarForm({ field: 'tipo', value: e }))
              }
            >
              <Option value="MATRICULA">Matrícula</Option>
              <Option value="CONCLUSAO">Conclusão</Option>
            </Select>
          </FormItem>
        </Show>
      </FormGrid>

      <FormActions>
        <Button
          type="primary"
          danger
          onClick={handleSubmit}
          loading={submitting}
          icon={<SendOutlined />}
          disabled={
            !cancelarForm.targetState ||
            !cancelarForm.modulo ||
            !cancelarForm.cpf ||
            !cancelarForm.dataNascimento ||
            !cancelarForm.cursoId
          }
        >
          Cancelar Matrícula
        </Button>
      </FormActions>
    </CancelarCard>
  )
}

const Container = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 24px;
`

const PageTitle = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const SearchCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const MatriculaCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const ActionsCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const MatricularCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const CancelarCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const MatriculaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`

const DatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

const MatriculaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DateItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`

const Value = styled.span`
  color: #666;
  font-size: 14px;
`

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`
